import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, getAccessToken, setAccessToken } from "./api";
import type { User } from "./types";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "authed"; user: User }
  | { status: "anonymous"; user: null };

type AuthContextValue = AuthState & {
  setSession: (token: string, user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });

  const refreshUser = async (): Promise<User | null> => {
    try {
      const user = await api.get<User>("/auth/me");
      setState({ status: "authed", user });
      return user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAccessToken(null);
        setState({ status: "anonymous", user: null });
        return null;
      }
      throw err;
    }
  };

  const setSession = (token: string, user: User) => {
    setAccessToken(token);
    setState({ status: "authed", user });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — clear locally anyway */
    }
    setAccessToken(null);
    setState({ status: "anonymous", user: null });
  };

  useEffect(() => {
    // Bootstrap: if no access token in memory, try refresh via cookie.
    let cancelled = false;
    (async () => {
      if (!getAccessToken()) {
        // /auth/me will trigger a refresh attempt via the api client interceptor.
      }
      try {
        const user = await api.get<User>("/auth/me");
        if (!cancelled) setState({ status: "authed", user });
      } catch {
        if (!cancelled) setState({ status: "anonymous", user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, setSession, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
